import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Select, Space, Typography } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";

import { useGetClassroomsPaginatedQuery } from "../../../store/api/classroomsApi";
import useDebounce from "../../../hooks/useDebounce";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const fmtDateTime = (iso: string | null | undefined): string =>
  iso && dayjs(iso).isValid() ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—";

/**
 * Búsqueda de alumno/usuario: texto libre que filtra la tabla directamente por
 * nombre, apellido, username o email (coincidencia parcial, SCRUM-1095) — no
 * es un selector de un usuario puntual, es el mismo texto que se manda como
 * filtro al backend. Cada tipeo, tras el debounce, se vuelca a la URL.
 */
export const StudentSearchInput: React.FC<{
  value: string;
  onCommit: (value: string | undefined) => void;
}> = ({ value, onCommit }) => {
  const [input, setInput] = useState(value);
  const debounced = useDebounce(input, 300);

  // El input local se resetea si la URL cambia desde afuera (ej. "Limpiar
  // filtros" o back/forward del navegador), pero no pisa un espacio que el
  // operador está tipeando: la URL guarda el texto recortado.
  useEffect(() => {
    setInput((current) => (current.trim() === value ? current : value));
  }, [value]);

  useEffect(() => {
    if (debounced.trim() === value) return;
    onCommit(debounced.trim() || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <Input.Search
      allowClear
      placeholder="Buscar alumno o usuario"
      style={{ width: 280 }}
      value={input}
      onChange={(e) => setInput(e.target.value)}
    />
  );
};

/** Búsqueda de grupo contra el backend (SCRUM-1095). */
export const ClassroomFilterSelect: React.FC<{
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}> = ({ value, onChange }) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const skip = debouncedSearch.trim().length < 2;
  const { data, isFetching } = useGetClassroomsPaginatedQuery(
    { page: 1, pageSize: 20, search: debouncedSearch.trim() },
    { skip },
  );

  // Pre-carga del grupo seleccionado por id para poder mostrar su código
  // cuando llegás con classroomId en la URL sin haber tipeado nada.
  const { data: selectedData } = useGetClassroomsPaginatedQuery(
    { page: 1, pageSize: 1, id: value },
    { skip: value === undefined },
  );

  const options = useMemo(() => {
    const map = new Map<number, { value: number; label: string }>();
    const add = (c: {
      id: number;
      custom_code: string;
      school?: { name: string } | null;
    }) => {
      if (map.has(c.id)) return;
      map.set(c.id, {
        value: c.id,
        label: c.school ? `${c.custom_code} · ${c.school.name}` : c.custom_code,
      });
    };
    (data?.data ?? []).forEach(add);
    const selected = selectedData?.data.find((c) => c.id === value);
    if (selected) add(selected);
    return Array.from(map.values());
  }, [data, selectedData, value]);

  return (
    <Select<number | undefined>
      allowClear
      showSearch
      placeholder="Filtrar por grupo"
      style={{ width: 220 }}
      value={value}
      onChange={(next) => {
        setSearch("");
        onChange(next);
      }}
      onSearch={setSearch}
      filterOption={false}
      loading={isFetching}
      notFoundContent={
        skip
          ? "Escribí al menos 2 caracteres…"
          : isFetching
            ? "Buscando…"
            : "Sin coincidencias"
      }
      options={options}
    />
  );
};

/**
 * ISO con límites del día explícitos: un "YYYY-MM-DD" pelado se parsea como
 * UTC-midnight y en TZ negativas se pierde casi todo el último día
 * seleccionado (lección heredada de la pantalla Activity).
 */
export const SessionDateRange: React.FC<{
  from: string | undefined;
  to: string | undefined;
  onChange: (range: { from?: string; to?: string }) => void;
}> = ({ from, to, onChange }) => {
  const value: RangePickerProps["value"] =
    from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null;

  return (
    <RangePicker
      value={value}
      onChange={(dates) =>
        onChange({
          from: dates?.[0] ? dates[0].startOf("day").toISOString() : undefined,
          to: dates?.[1] ? dates[1].endOf("day").toISOString() : undefined,
        })
      }
    />
  );
};

/** Nombre, username y email del alumno. Muchos alumnos no tienen email. */
export const StudentCell: React.FC<{
  user: {
    first_name: string;
    last_name: string;
    username: string | null;
    email: string | null;
  };
}> = ({ user }) => (
  <Space direction="vertical" size={0}>
    <Text>{`${user.first_name} ${user.last_name}`.trim() || "—"}</Text>
    {user.username && (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {user.username}
      </Text>
    )}
    {user.email && (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {user.email}
      </Text>
    )}
  </Space>
);
